import { CheckIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SubscribeDialog } from '@/components/organisms/subscribe-dialog'
import { useSubscription } from '@/contexts/subscription-context'
import { useIndividuals } from '@/hooks/useIndividuals'

import { Button } from '../ui/button'

type Accent = 'pion' | 'turkos' | 'viol'

// Same accent shadows as the home page's stale-data card (see
// components/organisms/home/stale-banner.tsx) — this is meant to read as the same kind of
// notification card, not a distinct one, so it reuses that exact recipe: white card, no
// border, just a soft accent shadow.
const ACCENTS: Record<Accent, { shadow: string }> = {
  pion: { shadow: 'rgba(178,58,102,0.18)' },
  turkos: { shadow: 'rgba(14,124,102,0.18)' },
  viol: { shadow: 'rgba(109,79,211,0.20)' },
}

// Same four lines shown in the subscribe dialog's "Included" column — reused rather than
// duplicated, so the sales pitch never drifts out of sync between the two places it appears.
const INCLUDED_KEYS = [
  'subscribeDialog.included.documents',
  'subscribeDialog.included.conversations',
  'subscribeDialog.included.findSupport',
  'subscribeDialog.included.sharedAccess',
] as const

/**
 * Surfaces the individual's entitlement: remaining trial uploads, or that the account
 * has gone read-only.
 *
 * Renders nothing on an active subscription — the normal case shouldn't carry a
 * persistent banner.
 *
 * Everyone with access to the individual sees the same card. Only the CTA is limited to
 * the owner — sending anyone else to checkout would have them pay for somebody else's
 * subscription, since the pool belongs to whoever created it.
 */
export const SubscriptionBanner = () => {
  const { t } = useTranslation()
  const { selectedIndividual } = useIndividuals()
  const { documentLimit, documentsRemaining, documentsUsed, isOwner, isReadOnly, isTrial } =
    useSubscription()
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false)

  if (!isReadOnly && (!isTrial || documentLimit === null)) return null

  const exhausted = isTrial && documentsRemaining === 0
  const accent: Accent = isReadOnly ? 'pion' : exhausted ? 'viol' : 'turkos'

  const heading = isReadOnly
    ? t('subscriptionBanner.expiredHeading')
    : exhausted
      ? t('subscriptionBanner.trialLimitHeading')
      : t('subscriptionBanner.trialHeading', { name: selectedIndividual?.firstName ?? '' })

  const intro = isReadOnly
    ? t('subscriptionBanner.expiredIntro')
    : exhausted
      ? t('subscriptionBanner.trialLimitIntro')
      : t('subscriptionBanner.trialIntro', { name: selectedIndividual?.firstName ?? '' })

  return (
    <div
      className="mx-auto flex w-full max-w-3xl flex-col gap-5 rounded-[20px] bg-white p-8 sm:flex-row sm:gap-8"
      style={{
        boxShadow: `0 1px 2px rgba(20,33,28,0.03), 0 14px 34px -20px ${ACCENTS[accent].shadow}`,
      }}
    >
      <div className="flex min-w-0 flex-col justify-between sm:flex-1">
        <div>
          <p className="text-[26px] font-bold leading-snug tracking-tight text-[#14211C]">
            {heading}
          </p>
          <p className="mt-1 text-[14.5px] leading-relaxed text-[#5B6B65]">{intro}</p>

          {!isReadOnly && isTrial && documentLimit !== null && (
            <span className="mt-2 inline-flex h-[30px] items-center rounded-full border border-[#E7ECEA] bg-[#F4F6F5] px-3 text-[13px] font-semibold text-[#2C3B35]">
              {t('subscriptionBanner.documentsChip', { limit: documentLimit, used: documentsUsed })}
            </span>
          )}
        </div>

        {isOwner && (
          <Button className="mt-4 w-full shrink-0 sm:w-auto" onClick={() => setIsSubscribeOpen(true)}>
            {t('subscriptionBanner.subscribeButton')}
          </Button>
        )}
      </div>

      <div className="flex flex-col justify-center gap-3 rounded-2xl bg-brand-gran p-5 text-white sm:flex-1 sm:-my-4 sm:-mr-4">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-white">
          {t('subscribeDialog.included.label')}
        </span>
        <ul className="flex flex-col gap-2">
          {INCLUDED_KEYS.map(key => (
            <li className="flex items-start gap-2 text-[15px] leading-snug" key={key}>
              <CheckIcon className="mt-0.5 size-3.5 shrink-0 text-brand-lime" strokeWidth={3} />
              {t(key)}
            </li>
          ))}
        </ul>
      </div>

      <SubscribeDialog onOpenChange={setIsSubscribeOpen} open={isSubscribeOpen} />
    </div>
  )
}
